import React, { useState, useEffect } from 'react';
import { freelancerAPI } from '../services/api';

const Freelancers = () => {
  const [freelancers, setFreelancers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({
    name:'', phone:'', email:'',
    city:'', notes:'',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);

  // Edit state
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name:'', phone:'', email:'',
    city:'', notes:'',
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');

  // Delete state
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadFreelancers();
  }, []);

  const loadFreelancers = async () => {
    setLoading(true);
    try {
      const res = await freelancerAPI.getAll();
      setFreelancers(res.data);
    } catch (err) {
      console.error('Failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = search
    ? freelancers.filter(f =>
        f.name.toLowerCase().includes(search.toLowerCase()) ||
        f.phone.includes(search) ||
        (f.city || '').toLowerCase().includes(search.toLowerCase())
      )
    : freelancers;

  const handleAdd = async () => {
    if (!form.name || !form.phone) {
      setError('Name and phone are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await freelancerAPI.add(form);
      setForm({ name:'', phone:'', email:'', city:'', notes:'' });
      setShowAdd(false);
      await loadFreelancers();
    } catch (err) {
      setError(
        err.response?.data?.error
        || err.response?.data?.message
        || 'Failed to add freelancer');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = () => {
    setEditForm({
      name: selected.name || '',
      phone: selected.phone || '',
      email: selected.email || '',
      city: selected.city || '',
      notes: selected.notes || '',
    });
    setEditError('');
    setEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm.name) {
      setEditError('Name is required');
      return;
    }
    if (!editForm.phone) {
      setEditError('Phone is required');
      return;
    }
    setSavingEdit(true);
    setEditError('');
    try {
      const res = await freelancerAPI.update(selected.id, editForm);
      const updated = res.data;
      setSelected(prev => ({ ...prev, ...updated }));
      setEditing(false);
      await loadFreelancers();
    } catch (err) {
      setEditError(
        err.response?.data?.error
        || err.response?.data?.message
        || 'Failed to update freelancer');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(
      'Remove this freelancer from your directory? ' +
      'Any tickets already assigned to them will keep ' +
      'their history, but you will not be able to assign ' +
      'new tasks to them unless re-added.'
    )) return;
    setDeleting(true);
    try {
      await freelancerAPI.delete(id);
      await loadFreelancers();
      setSelected(null);
    } catch (err) {
      alert(
        err.response?.data?.error
        || err.response?.data?.message
        || 'Failed to delete freelancer');
    } finally {
      setDeleting(false);
    }
  };

  const inputStyle = {
    width:'100%',
    background:'var(--surface2)',
    border:'1px solid var(--border2)',
    color:'var(--text)',
    padding:'10px 14px',
    borderRadius:'8px',
    fontSize:'13.5px',
    outline:'none',
  };

  const labelStyle = {
    display:'block',
    fontSize:'11px',
    fontFamily:'DM Mono,monospace',
    color:'var(--text3)',
    letterSpacing:'1px',
    textTransform:'uppercase',
    marginBottom:'6px',
  };

  const smallInputStyle = {
    width:'100%',
    background:'var(--surface)',
    border:'1px solid var(--border2)',
    color:'var(--text)',
    padding:'8px 10px',
    borderRadius:'6px',
    fontSize:'12.5px',
    outline:'none',
    marginBottom:'10px',
  };

  const smallLabelStyle = {
    display:'block',
    fontSize:'10px',
    fontFamily:'DM Mono,monospace',
    color:'var(--text3)',
    letterSpacing:'0.5px',
    textTransform:'uppercase',
    marginBottom:'4px',
  };

  return (
    <div style={{ padding:'24px' }}>
      {/* Header */}
      <div style={{
        display:'flex',
        justifyContent:'space-between',
        alignItems:'center',
        marginBottom:'24px',
      }}>
        <div>
          <h1 style={{
            fontFamily:'Syne,sans-serif',
            fontSize:'22px', fontWeight:'800',
          }}>
            Freelancers
          </h1>
          <p style={{
            color:'var(--text3)',
            fontSize:'13px', marginTop:'4px',
          }}>
            Manage sub-vendors and freelancers for one-off tasks
          </p>
        </div>
        <button
          onClick={() => setShowAdd(!showAdd)}
          style={{
            background:'var(--accent)',
            color:'var(--bg)',
            border:'none',
            padding:'10px 20px',
            borderRadius:'8px',
            fontSize:'13px',
            fontWeight:'600',
            cursor:'pointer',
          }}
        >
          + Add Freelancer
        </button>
      </div>

      {/* Add Freelancer Form */}
      {showAdd && (
        <div style={{
          background:'var(--surface)',
          border:'1px solid var(--border)',
          borderRadius:'12px',
          padding:'24px',
          marginBottom:'20px',
        }}>
          <div style={{
            fontFamily:'Syne,sans-serif',
            fontSize:'15px', fontWeight:'700',
            marginBottom:'20px',
          }}>
            Add New Freelancer
          </div>

          <div style={{
            display:'grid',
            gridTemplateColumns:'1fr 1fr',
            gap:'16px',
          }}>
            <div>
              <label style={labelStyle}>Full Name *</label>
              <input
                style={inputStyle}
                placeholder="Freelancer or sub-vendor name"
                value={form.name}
                onChange={e => setForm({ ...form, name:e.target.value })}
              />
            </div>
            <div>
              <label style={labelStyle}>Phone Number *</label>
              <input
                style={inputStyle}
                placeholder="10 digit phone"
                value={form.phone}
                onChange={e => setForm({ ...form, phone:e.target.value })}
              />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input
                style={inputStyle}
                placeholder="Email (optional)"
                value={form.email}
                onChange={e => setForm({ ...form, email:e.target.value })}
              />
            </div>
            <div>
              <label style={labelStyle}>City</label>
              <input
                style={inputStyle}
                placeholder="e.g. Bhopal, Mangalore"
                value={form.city}
                onChange={e => setForm({ ...form, city:e.target.value })}
              />
            </div>
            <div style={{ gridColumn:'1 / -1' }}>
              <label style={labelStyle}>Notes</label>
              <input
                style={inputStyle}
                placeholder="Optional notes about this freelancer"
                value={form.notes}
                onChange={e => setForm({ ...form, notes:e.target.value })}
              />
            </div>
          </div>

          {error && (
            <div style={{
              background:'rgba(255,77,109,0.1)',
              border:'1px solid rgba(255,77,109,0.3)',
              color:'var(--red)',
              padding:'10px 14px',
              borderRadius:'8px',
              fontSize:'13px',
              marginTop:'16px',
            }}>
              {error}
            </div>
          )}

          <div style={{
            display:'flex',
            gap:'10px',
            marginTop:'20px',
          }}>
            <button
              onClick={handleAdd}
              disabled={saving}
              style={{
                background:'var(--accent)',
                color:'var(--bg)',
                border:'none',
                padding:'10px 24px',
                borderRadius:'8px',
                fontSize:'13px',
                fontWeight:'600',
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Saving...' : 'Save Freelancer'}
            </button>
            <button
              onClick={() => {
                setShowAdd(false);
                setError('');
              }}
              style={{
                background:'transparent',
                color:'var(--text2)',
                border:'1px solid var(--border2)',
                padding:'10px 24px',
                borderRadius:'8px',
                fontSize:'13px',
                cursor:'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Search */}
      <div style={{
        display:'flex', gap:'10px',
        marginBottom:'16px',
      }}>
        <div style={{
          display:'flex',
          alignItems:'center',
          background:'var(--surface2)',
          border:'1px solid var(--border2)',
          borderRadius:'8px',
          padding:'0 12px',
          gap:'8px', flex:1,
          maxWidth:'300px',
        }}>
          <span style={{ color:'var(--text3)' }}>⊞</span>
          <input
            placeholder="Search freelancers..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              background:'none',
              border:'none', outline:'none',
              color:'var(--text)',
              fontSize:'13px',
              padding:'9px 0', flex:1,
            }}
          />
        </div>
      </div>

      {/* Main Layout */}
      <div style={{
        display:'grid',
        gridTemplateColumns: selected ? '1fr 340px' : '1fr',
        gap:'16px',
      }}>
        {/* Freelancers Grid */}
        <div>
          {loading ? (
            <div style={{
              padding:'40px',
              textAlign:'center',
              color:'var(--text3)',
              fontFamily:'DM Mono,monospace',
            }}>
              Loading freelancers...
            </div>
          ) : filtered.length === 0 ? (
            <div style={{
              background:'var(--surface)',
              border:'1px solid var(--border)',
              borderRadius:'12px',
              padding:'60px',
              textAlign:'center',
              color:'var(--text3)',
            }}>
              <div style={{ fontSize:'40px', marginBottom:'12px' }}>◈</div>
              <div style={{ fontSize:'14px', color:'var(--text2)', marginBottom:'6px' }}>
                No freelancers yet
              </div>
              <div style={{ fontSize:'12px' }}>
                Click "+ Add Freelancer" to build your directory of
                sub-vendors for one-off jobs outside your reach
              </div>
            </div>
          ) : (
            <div style={{
              display:'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap:'14px',
            }}>
              {filtered.map(f => (
                <div key={f.id}
                  onClick={() => {
                    setSelected(selected?.id === f.id ? null : f);
                    setEditing(false);
                  }}
                  style={{
                    background:'var(--surface)',
                    border: selected?.id === f.id
                      ? '1px solid var(--accent)'
                      : '1px solid var(--border)',
                    borderRadius:'12px',
                    padding:'18px',
                    cursor:'pointer',
                    transition:'all 0.15s',
                  }}
                >
                  <div style={{
                    display:'flex',
                    alignItems:'center',
                    gap:'12px',
                    marginBottom:'14px',
                  }}>
                    <div style={{
                      width:'44px',
                      height:'44px',
                      borderRadius:'10px',
                      background:'linear-gradient(135deg, var(--purple), var(--accent))',
                      display:'flex',
                      alignItems:'center',
                      justifyContent:'center',
                      fontFamily:'Syne,sans-serif',
                      fontSize:'16px',
                      fontWeight:'800',
                      color:'var(--bg)',
                    }}>
                      {f.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                    </div>
                    <div>
                      <div style={{
                        fontFamily:'Syne,sans-serif',
                        fontSize:'15px',
                        fontWeight:'700',
                      }}>
                        {f.name}
                      </div>
                      <div style={{
                        fontSize:'11px',
                        fontFamily:'DM Mono,monospace',
                        color:'var(--text3)',
                        marginTop:'2px',
                      }}>
                        {f.city || 'No city set'}
                      </div>
                    </div>
                  </div>

                  <div style={{
                    display:'flex',
                    justifyContent:'space-between',
                    alignItems:'center',
                    paddingTop:'12px',
                    borderTop:'1px solid var(--border)',
                    fontSize:'12px',
                  }}>
                    <span style={{
                      color:'var(--text3)',
                      fontFamily:'DM Mono,monospace',
                    }}>
                      {f.phone}
                    </span>
                    {f.email && (
                      <span style={{
                        background:'var(--surface3)',
                        color:'var(--text2)',
                        padding:'2px 8px',
                        borderRadius:'4px',
                        fontSize:'11px',
                      }}>
                        {f.email}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Detail Panel */}
        {selected && (
          <div style={{
            background:'var(--surface)',
            border:'1px solid var(--border)',
            borderRadius:'12px',
            overflow:'hidden',
            height:'fit-content',
          }}>
            <div style={{
              padding:'16px 20px',
              borderBottom:'1px solid var(--border)',
              display:'flex',
              justifyContent:'space-between',
              alignItems:'center',
            }}>
              <div style={{
                fontFamily:'Syne,sans-serif',
                fontSize:'14px',
                fontWeight:'700',
              }}>
                Freelancer Details
              </div>
              <div style={{
                display:'flex',
                alignItems:'center',
                gap:'12px',
              }}>
                {!editing && (
                  <button
                    onClick={openEdit}
                    style={{
                      background:'none',
                      border:'none',
                      color:'var(--accent)',
                      cursor:'pointer',
                      fontSize:'12px',
                      fontWeight:'600',
                    }}
                  >
                    Edit
                  </button>
                )}
                <button
                  onClick={() => {
                    setSelected(null);
                    setEditing(false);
                  }}
                  style={{
                    background:'none',
                    border:'none',
                    color:'var(--text3)',
                    cursor:'pointer',
                    fontSize:'18px',
                  }}
                >
                  ✕
                </button>
              </div>
            </div>

            <div style={{ padding:'20px' }}>
              {editing ? (
                <>
                  <div>
                    <label style={smallLabelStyle}>Full Name *</label>
                    <input
                      style={smallInputStyle}
                      value={editForm.name}
                      onChange={e => setEditForm({ ...editForm, name:e.target.value })}
                    />
                  </div>
                  <div>
                    <label style={smallLabelStyle}>Phone Number *</label>
                    <input
                      style={smallInputStyle}
                      value={editForm.phone}
                      onChange={e => setEditForm({ ...editForm, phone:e.target.value })}
                    />
                    <div style={{
                      fontSize:'10.5px',
                      color:'var(--text3)',
                      marginTop:'-4px',
                      marginBottom:'10px',
                    }}>
                      This is the phone number the freelancer will use
                      to verify themselves when opening a task link.
                    </div>
                  </div>
                  <div>
                    <label style={smallLabelStyle}>Email</label>
                    <input
                      style={smallInputStyle}
                      value={editForm.email}
                      onChange={e => setEditForm({ ...editForm, email:e.target.value })}
                    />
                  </div>
                  <div>
                    <label style={smallLabelStyle}>City</label>
                    <input
                      style={smallInputStyle}
                      value={editForm.city}
                      onChange={e => setEditForm({ ...editForm, city:e.target.value })}
                    />
                  </div>
                  <div>
                    <label style={smallLabelStyle}>Notes</label>
                    <input
                      style={smallInputStyle}
                      value={editForm.notes}
                      onChange={e => setEditForm({ ...editForm, notes:e.target.value })}
                    />
                  </div>

                  {editError && (
                    <div style={{
                      background:'rgba(255,77,109,0.1)',
                      border:'1px solid rgba(255,77,109,0.3)',
                      color:'var(--red)',
                      padding:'8px 10px',
                      borderRadius:'6px',
                      fontSize:'12px',
                      marginBottom:'10px',
                    }}>
                      {editError}
                    </div>
                  )}

                  <div style={{ display:'flex', gap:'8px' }}>
                    <button
                      onClick={handleSaveEdit}
                      disabled={savingEdit}
                      style={{
                        flex:1,
                        background:'var(--accent)',
                        color:'var(--bg)',
                        border:'none',
                        padding:'9px',
                        borderRadius:'6px',
                        fontSize:'12.5px',
                        fontWeight:'600',
                        cursor: savingEdit ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {savingEdit ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setEditing(false);
                        setEditError('');
                      }}
                      style={{
                        flex:1,
                        background:'transparent',
                        color:'var(--text2)',
                        border:'1px solid var(--border2)',
                        padding:'9px',
                        borderRadius:'6px',
                        fontSize:'12.5px',
                        cursor:'pointer',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {[
                    ['Name', selected.name],
                    ['Phone', selected.phone],
                    ['Email', selected.email || '—'],
                    ['City', selected.city || '—'],
                    ['Notes', selected.notes || '—'],
                  ].map(([label, value]) => (
                    <div key={label} style={{
                      display:'flex',
                      justifyContent:'space-between',
                      padding:'8px 0',
                      borderBottom:'1px solid var(--border)',
                      fontSize:'12.5px',
                    }}>
                      <span style={{
                        color:'var(--text3)',
                        fontFamily:'DM Mono,monospace',
                        fontSize:'11px',
                      }}>
                        {label}
                      </span>
                      <span style={{
                        color:'var(--text)',
                        fontWeight:'500',
                        textAlign:'right',
                        maxWidth:'65%',
                      }}>
                        {value}
                      </span>
                    </div>
                  ))}

                  <button
                    onClick={() => handleDelete(selected.id)}
                    disabled={deleting}
                    style={{
                      width:'100%',
                      background:'rgba(255,77,109,0.1)',
                      color:'var(--red)',
                      border:'1px solid rgba(255,77,109,0.3)',
                      padding:'10px',
                      borderRadius:'8px',
                      fontSize:'13px',
                      fontWeight:'600',
                      cursor: deleting ? 'not-allowed' : 'pointer',
                      marginTop:'16px',
                    }}
                  >
                    {deleting ? 'Deleting...' : 'Delete Freelancer'}
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Freelancers;
